import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Snackbar,
  Typography,
} from '@mui/material';
import { api } from '../../super-picker/services/api';
import SuperConnectorInterestDialog, { ConnectorInterestUser } from '../../super-picker/dialogs/others/SuperConnectorInterestDialog';
import DistinctiveDialog from '../../super-picker/dialogs/others/DistinctiveDialog';
import { DISTINCTIVE_TEMPLATES } from '../../super-picker/distinctive/distinctiveTemplates';

const SUPER_CONNECTOR_TEMPLATES = DISTINCTIVE_TEMPLATES.filter(
  (template) => template.id === 'super-connector',
);

interface Props {
  /** When false, children render with no lock. */
  locked: boolean;
  user: ConnectorInterestUser;
  children: React.ReactNode;
}

/**
 * Locks Configuration pages for no-access users. The visible overlay is only
 * the explanation; clicks stay blocked if that node is removed, because a
 * document-level capture listener ignores every event whose target sits in
 * the page content.
 */
export default function PremiumConfigGate({ locked, user, children }: Props) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [checking, setChecking] = useState(false);
  const [edmOpen, setEdmOpen] = useState(false);
  const [interestOpen, setInterestOpen] = useState(false);
  const [registered, setRegistered] = useState(false);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    if (locked) el.setAttribute('inert', '');
    else el.removeAttribute('inert');
  }, [locked]);

  useEffect(() => {
    if (!locked) return;
    const stop = (event: Event) => {
      const root = contentRef.current;
      const target = event.target;
      if (!root || !(target instanceof Node) || !root.contains(target)) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    };
    const types = ['pointerdown', 'mousedown', 'click', 'dblclick', 'auxclick', 'keydown', 'keyup', 'submit', 'change', 'input', 'beforeinput'] as const;
    types.forEach((type) => document.addEventListener(type, stop, true));
    return () => {
      types.forEach((type) => document.removeEventListener(type, stop, true));
    };
  }, [locked]);

  const openAppointment = useCallback(async () => {
    setChecking(true);
    try {
      const result = await api.getConnectorInterestEligibility();
      if (result.eligible) setInterestOpen(true);
      else setEdmOpen(true);
    } catch {
      setEdmOpen(true);
    } finally {
      setChecking(false);
    }
  }, []);

  if (!locked) return <>{children}</>;

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        ref={contentRef}
        component="fieldset"
        disabled
        aria-hidden
        sx={{
          border: 0,
          p: 0,
          m: 0,
          minWidth: 0,
          pointerEvents: 'none',
          userSelect: 'none',
          '&:disabled': { opacity: 1 },
        }}
      >
        {children}
      </Box>

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 10,
          bgcolor: 'rgba(15, 23, 42, 0.55)',
          backdropFilter: 'blur(1px)',
        }}
      >
        <Box
          sx={{
            position: 'sticky',
            top: 24,
            display: 'flex',
            justifyContent: 'center',
            px: 2,
            pt: 3,
          }}
        >
          <Paper
            elevation={6}
            sx={{
              maxWidth: 440,
              width: '100%',
              p: { xs: 2.5, sm: 3 },
              textAlign: 'center',
              borderTop: '3px solid #ed8936',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Premium features
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5, lineHeight: 1.6 }}>
              These are premium features. They require payment and setup before you can use them.
            </Typography>
            <Button
              variant="contained"
              onClick={openAppointment}
              disabled={checking}
              sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none', fontWeight: 700 }}
            >
              {checking ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Schedule a setup appointment'}
            </Button>
          </Paper>
        </Box>
      </Box>

      <DistinctiveDialog
        open={edmOpen}
        onClose={() => setEdmOpen(false)}
        user={user}
        templates={SUPER_CONNECTOR_TEMPLATES}
        title="Super Connector"
        description="Download or share your personalized EDM to unlock the setup appointment."
        values={{
          agentLine: [user.name, user.cea, user.mobile || user.email]
            .map((part) => part.trim())
            .filter(Boolean)
            .join(' · '),
          agencyLine: 'Huttons Asia Pte Ltd · L3008899K',
        }}
        onRegistered={() => setRegistered(true)}
      />
      <SuperConnectorInterestDialog
        open={interestOpen}
        onClose={() => setInterestOpen(false)}
        user={user}
        onRegistered={() => {
          setInterestOpen(false);
          setRegistered(true);
        }}
      />
      <Snackbar open={registered} autoHideDuration={5000} onClose={() => setRegistered(false)}>
        <Alert severity="success" onClose={() => setRegistered(false)}>
          Interest registered — we'll see you at your appointment!
        </Alert>
      </Snackbar>
    </Box>
  );
}
